// Factory function pattern to avoid Webpack TDZ issues with class inheritance
// This uses BaseNotaryFactory instead of class inheritance

import { createBaseNotary, type IBaseNotary } from "../BaseNotaryFactory";
import type { Office } from "le-coffre-resources/dist/Notary";
import { createNamedFileFormData } from "@Front/Api/BaseApiServiceFactory/helpers/formDataRequestHelpers";
import { ContentType } from "@Front/Api/ContentType";

interface OfficesInstance {
	getByUid(uid: string, params?: { q?: Record<string, unknown> }): Promise<Office>;
	uploadRIB(officeUid: string, ribFile: File): Promise<{ message: string; office: Office }>;
	downloadRIB(officeUid: string): Promise<Blob>;
	checkRIBStatus(officeUid: string): Promise<{ hasRIB: boolean }>;
	namespaceUrl: string;
}

// Module-level singleton instance
let instance: OfficesInstance | null = null;
let baseNotaryInstance: IBaseNotary | null = null;

async function uploadRIBImpl(
	officesUrl: string,
	baseNotary: IBaseNotary,
	officeUid: string,
	ribFile: File,
): Promise<{ message: string; office: Office }> {
	const url = new URL(officesUrl.concat(`/${officeUid}/rib`));
	const formData = createNamedFileFormData("rib", ribFile);
	try {
		return await baseNotary.postRequestFormData<{ message: string; office: Office }>(url, formData);
	} catch (err) {
		baseNotary.onError(err);
		return Promise.reject(err);
	}
}

async function downloadRIBImpl(officesUrl: string, baseNotary: IBaseNotary, officeUid: string): Promise<Blob> {
	const url = new URL(officesUrl.concat(`/${officeUid}/rib`));
	try {
		return await baseNotary.getRequest<Blob>(url, { contentType: ContentType.PDF });
	} catch (err) {
		baseNotary.onError(err);
		return Promise.reject(err);
	}
}

async function checkRIBStatusImpl(officesUrl: string, baseNotary: IBaseNotary, officeUid: string): Promise<{ hasRIB: boolean }> {
	const url = new URL(officesUrl.concat(`/${officeUid}/rib/status`));
	try {
		return await baseNotary.getRequest<{ hasRIB: boolean }>(url);
	} catch (err) {
		baseNotary.onError(err);
		return Promise.reject(err);
	}
}

function createOffices(): OfficesInstance {
	if (!baseNotaryInstance) {
		baseNotaryInstance = createBaseNotary();
	}

	const baseNotary = baseNotaryInstance;
	const officesUrl = baseNotary.namespaceUrl.concat("/offices");

	return {
		namespaceUrl: baseNotary.namespaceUrl,
		getByUid: async (uid: string, params?: { q?: Record<string, unknown> }): Promise<Office> => {
			const url = new URL(officesUrl.concat(`/${uid}`));
			if (params?.q) {
				baseNotary.appendJsonSearchParams(url, params.q);
			}
			return await baseNotary.getRequest<Office>(url);
		},
		uploadRIB: (officeUid: string, ribFile: File) => uploadRIBImpl(officesUrl, baseNotary, officeUid, ribFile),
		downloadRIB: (officeUid: string) => downloadRIBImpl(officesUrl, baseNotary, officeUid),
		checkRIBStatus: (officeUid: string) => checkRIBStatusImpl(officesUrl, baseNotary, officeUid),
	};
}

export function getOfficesInstance(): OfficesInstance {
	if (!instance) {
		instance = createOffices();
	}
	return instance;
}
